// nemAnalysisService.ts (frontend)
// API client for the Site NEM Analysis stepped pipeline.
// Backend routes: server/features/billValidation/routes/billValidation.ts

import { supabase } from '../../../foundation/altzero_base/base/utils/supabaseClient';
import type { NemAnalysisRun, ApplyToBillResult } from '../types/nemAnalysisTypes';
import type { TouSlot } from '../types/nemTypes';

type ApiResponse<T> = { success: boolean; data?: T; error?: string };

const API_BASE = `${import.meta.env.VITE_API_URL ?? 'http://localhost:3001'}/api/bill-validation`;

async function request<T>(path: string, init?: RequestInit): Promise<ApiResponse<T>> {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    const res = await fetch(`${API_BASE}${path}`, {
      ...init,
      headers: {
        'Content-Type': 'application/json',
        'x-api-key': 'scopingai',
        'x-user-id': session?.user?.id ?? '',
        ...(init?.headers ?? {}),
      },
    });
    const json = await res.json() as ApiResponse<T>;
    if (!res.ok || !json.success) {
      return { success: false, error: json.error ?? `Request failed (${res.status})` };
    }
    return json;
  } catch (error: unknown) {
    return { success: false, error: (error as Error).message };
  }
}

export const nemAnalysisService = {
  /** Create (or fetch) the analysis run for a site + period. */
  getOrCreateRun: (siteId: string, orgId: string, periodFrom: string, periodTo: string) =>
    request<NemAnalysisRun>(`/sites/${siteId}/nem-analysis`, {
      method: 'POST',
      body: JSON.stringify({ org_id: orgId, period_from: periodFrom, period_to: periodTo }),
    }),

  /** Fetch the run for a site + period if one exists (null data when none). */
  getRunForPeriod: (siteId: string, orgId: string, periodFrom: string, periodTo: string) =>
    request<NemAnalysisRun | null>(
      `/sites/${siteId}/nem-analysis?org_id=${encodeURIComponent(orgId)}&period_from=${periodFrom}&period_to=${periodTo}`,
    ),

  getRun: (runId: string) => request<NemAnalysisRun>(`/nem-analysis/${runId}`),

  /** Step 1 — load GCS-synced meter data + coverage. */
  loadData: (runId: string) =>
    request<NemAnalysisRun>(`/nem-analysis/${runId}/load-data`, { method: 'POST' }),

  /** Step 2 — network tariff charges. `force` reprocesses days already stamped
   *  dist_processed_at; without it the RPC skips them and reports "up to date",
   *  which is wrong after a tariff or rate change. */
  runTariff: (runId: string, force = false) =>
    request<NemAnalysisRun>(`/nem-analysis/${runId}/tariff`, {
      method: 'POST',
      body: JSON.stringify({ force }),
    }),

  /** Step 3 — retail TOU charges. `force` as above, for tou_processed_at. */
  runTou: (runId: string, force = false) =>
    request<NemAnalysisRun>(`/nem-analysis/${runId}/tou`, {
      method: 'POST',
      body: JSON.stringify({ force }),
    }),

  /** Per-slot band detail (regenerated server-side; not persisted). */
  getTouSlots: (runId: string, nmi: string, suffix: string) =>
    request<TouSlot[]>(
      `/nem-analysis/${runId}/tou-slots?nmi=${encodeURIComponent(nmi)}&suffix=${encodeURIComponent(suffix)}`,
    ),

  /** Step 4 — write retail TOU + network buckets onto a bill. */
  applyToBill: (runId: string, billId: string) =>
    request<ApplyToBillResult>(`/nem-analysis/${runId}/apply-to-bill`, {
      method: 'POST',
      body: JSON.stringify({ bill_id: billId }),
    }),

  /** Bulk mode: run steps 1-3 for many sites (fire-and-forget; poll progress). */
  startBulk: (orgId: string, periodFrom: string, periodTo: string, siteIds?: string[]) =>
    request<{ bulkRunId: string; siteCount: number }>(`/nem-analysis/bulk`, {
      method: 'POST',
      body: JSON.stringify({ org_id: orgId, period_from: periodFrom, period_to: periodTo, site_ids: siteIds }),
    }),

  getBulkRun: (bulkRunId: string) =>
    request<{ run: Record<string, unknown>; analyses: Array<Record<string, unknown>> }>(
      `/nem-analysis/bulk/${bulkRunId}`,
    ),
};
